import React from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { RootState } from '../../../../reducers';
import { Grid, FormLabel, FormControl, TextField, Typography, Paper, Stack, RadioGroup, FormControlLabel, Radio, Divider } from '@mui/material';
import { DiagnosticType } from '../../../../Enum/DiagnosticType';
import { MiniMentalExamItem } from '../../../../Models/MiniMentalExams/MiniMentalExamItem.model';
import { copyObject } from '../../../../utils/common';
import { setSuicidalHomicidalIdeations } from '../../../../store/assessment.slice';
import { isAIIcon } from '../../Assessment';

const SuicidalIdeation: React.FC<{ memberId: string; setIsChanged: React.Dispatch<React.SetStateAction<boolean>> }> = ({ memberId, setIsChanged }) => {
    const dispatch = useAppDispatch();
    const { suicidalHomicidalIdeations } = useSelector((state: RootState) => state.assessment);
    const { engagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const isBHDiagnostic = engagementDraft?.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;

    const HIGH = 'High';
    const MODERATE = 'Moderate';
    const SUICIDE_RISK = 'Suicide Risk';

    const dtsQuestionsByType = [
        {
            type: 'Suicide',
            name: 'Suicide Protocol',
            list: [
                { name: 'SuicidalIdeation', label: 'Suicidal  ideation', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'Frequency', label: 'Frequency', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'Intention', label: 'Intention', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'TimeFrame', label: 'Time frame', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'Plan', label: 'Plan', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'LethalityOfMeans', label: 'Lethality of means', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'PreviousSuicideAttempts', label: 'Previous suicide attempts', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'RecentAttempts', label: 'Recent attempts', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'CaregiversAbilityToMonitorClient', label: 'Caregivers ability to monitor client', isMultiSelect: false, isRequired: isBHDiagnostic },
            ],
        },
        {
            type: 'SuicideRiskType',
            name: 'Suicide Risk Assessment',
            list: [
                { name: 'SuicideRisk', label: 'Suicide risk', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'RecentLoss', label: 'Recent loss', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'FamilyHistoryOfSuicide', label: 'Family history of suicide', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'EvidenceHistoryOfSelfMutilation', label: 'Evidence/history of self-mutilation', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'OtherSelfHarm', label: 'Other self-harm', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'WillKeepSelfSafe', label: 'Is member willing to say they will keep self-safe?', isMultiSelect: false, isRequired: isBHDiagnostic },
            ],
        },
    ];

    const handleOnChangeSIHIItem = (value: any, typePropsName: string, parentPropsName: string, childPropsName: string, saveLocal = false) => {
        let data = copyObject(suicidalHomicidalIdeations);
        data[typePropsName][parentPropsName][childPropsName] = value;
        if (data[typePropsName][parentPropsName]) {
            data[typePropsName][parentPropsName].IsAIAnswer = false;
        }
        dispatch(setSuicidalHomicidalIdeations(data));
        setIsChanged(true);
    };

    return (
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
            <Typography fontStyle={'italic'} color={'error.main'}>
                If past and current concerns reported, complete suicide protocol/Suicide risk assessment:
            </Typography>
            <Paper sx={{ p: 2, mt: 2, width: '80%' }} elevation={2}>
                <Stack spacing={2}>
                    {dtsQuestionsByType.map((type) => {
                        const list = type.list;
                        return (
                            <div key={`dto-item-${type.type}`}>
                                <Typography variant="body1" component="label" fontWeight={'bold'}>
                                    {type.name}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                {list.map((item, itemIndex) => {
                                    const dtsItem = suicidalHomicidalIdeations?.[type.type]?.[item.name] as MiniMentalExamItem;
                                    if (!dtsItem) return null;
                                    return (
                                        <Stack spacing={1} mb={2} key={`dts-item-${itemIndex}`}>
                                            <FormControl fullWidth size="small" key={`dts-item-${itemIndex}`}>
                                                <FormLabel required={isBHDiagnostic && item.isRequired} id={`${item.name}-type-select-label`}>
                                                    {item.label} {dtsItem?.IsAIAnswer && isAIIcon()}
                                                </FormLabel>
                                                <RadioGroup
                                                    row
                                                    aria-labelledby={`${item.name}-type-select-label`}
                                                    name={`${item.name}-type-select-label`}
                                                    value={Boolean(dtsItem?.Type?.Id) ? dtsItem.Type.Id : ''}
                                                    onChange={(e) => {
                                                        handleOnChangeSIHIItem(
                                                            dtsItem.TypeList.find((t) => t.Id === e.target.value),
                                                            type.type,
                                                            item.name,
                                                            'Type'
                                                        );
                                                    }}
                                                >
                                                    {dtsItem?.TypeList?.map((type) => (
                                                        <FormControlLabel key={type.Id} value={type.Id} control={<Radio />} label={type.Name} />
                                                    ))}
                                                </RadioGroup>
                                            </FormControl>
                                            {dtsItem.Name === SUICIDE_RISK && (dtsItem.Type?.Name === MODERATE || dtsItem.Type?.Name === HIGH) && (
                                                <TextField
                                                    label="Details if Moderate or High"
                                                    fullWidth
                                                    variant="outlined"
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    value={Boolean(dtsItem?.Description) ? dtsItem?.Description : ''}
                                                    onChange={(e) => {
                                                        handleOnChangeSIHIItem(e.target.value, type.type, item.name, 'Description', true);
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    );
                                })}
                            </div>
                        );
                    })}
                </Stack>
            </Paper>
        </Grid>
    );
};

export default SuicidalIdeation;
